import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Reason } from "@bitory-gmbh/nidavellir";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { backendApi } from "@/services/backend/api";
import { Confirmation } from "@/services/backend/rlt/confirmations/types/confirmation";
import { ConfirmationBasePatchRequest } from "@/services/backend/documents/types/confirmation";
import { updateCache } from "@/services/backend/_lib/update-cache";

export const rltConfirmationApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltConfirmationCreate: build.mutation<Confirmation, CreateRequest>({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "RltConfirmation", id: "LIST" },
        "RltConfirmation",
        "RltPricingByArea",
      ],
    }),
    rltConfirmationCreateFromOld: build.mutation<
      Confirmation,
      CreateFromOldRequest
    >({
      query: (request) => ({
        url: "/rlt/confirmation/create-from-old",
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "RltConfirmation", id: "LIST" },
        "RltConfirmation",
        "RltPricingByArea",
      ],
    }),
    rltConfirmationCreateFromOffer: build.mutation<
      Confirmation,
      CreateFromOfferRequest
    >({
      query: (request) => ({
        url: "/rlt/confirmation/create-from-offer",
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "RltConfirmation", id: "LIST" },
        "RltConfirmation",
        "RltPricingByArea",
      ],
    }),
    rltConfirmationShow: build.query<Confirmation, ShowRequest>({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltConfirmation", id: result.id }] : [],
    }),
    rltConfirmationList: build.query<Lst<Confirmation>, Partial<ListRequest>>({
      query: (request) => ({
        url: `/rlt/confirmation/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "RltConfirmation"),
    }),
    rltConfirmationPatch: build.mutation<Confirmation, PatchRequest>({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltConfirmation", id: request.id },
        "RltConfirmation",
        "RltCalculation",
        "RltTreatment",
        "RltPricing",
        "RltPricingByArea",
      ],
    }),
    rltConfirmationPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_r, _e, request) => [
        { type: "RltConfirmation", id: request.id },
      ],
    }),
    rltConfirmationPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this mutation will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
    }),
    rltConfirmationSetImmutable: build.mutation<
      Confirmation,
      SetImmutableRequest
    >({
      query: (request) => ({
        url: `/rlt/confirmation/set-immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "RltConfirmation", id: request.id },
        "RltConfirmation",
        "NumbersDocumentNumber",
      ],
    }),
    rltConfirmationDelete: build.mutation<Confirmation, DeleteRequest>({
      query: (request) => ({
        url: `/rlt/confirmation`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (result, e, request) => [
        { type: "RltConfirmation", id: request.id },
        "RltConfirmation",
      ],
    }),
  }),
});

export const {
  useRltConfirmationShowQuery,
  useRltConfirmationCreateMutation,
  useRltConfirmationCreateFromOfferMutation,
  useRltConfirmationCreateFromOldMutation,
  useRltConfirmationListQuery,
  useRltConfirmationDeleteMutation,
  useRltConfirmationPatchMutation,
  useRltConfirmationSetImmutableMutation,
  useRltConfirmationPdfPreviewQuery,
  useRltConfirmationPdfDownloadMutation,
} = rltConfirmationApi;

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOfferRequest {
  offerId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOldRequest {
  oldConfirmationId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface DeleteRequest {
  id: string;
}

export type PatchRequest = {
  id: string;
  internalComment?: string;
  serviceAddress?: Address | null;
  serviceContact?: Contact | null;
} & ConfirmationBasePatchRequest;

export interface PDFRequest {
  id: string;
  document: ConfirmationDocument;
}

export interface SetImmutableRequest {
  id: string;
  reason: Reason;
  setBy: string;
}

export enum ConfirmationImmutabilityReason {
  SetByUser = "set_by_user",
}

export enum ConfirmationDocument {
  All = "all",
  Letter = "letter",
  Treatment = "treatment",
  TreatmentByArea = "treatment-by-area",
}

/**
 * Patches the cached confirmation for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateRltConfirmationCache = updateCache(
  rltConfirmationApi,
  "rltConfirmationShow",
);
